<template>
  <div
    ref="rootRef"
    class="nav-item"
    :class="{
    'nav-item--active': isActive,
    'nav-item--focus-target': isFocusTarget,
    'nav-item--has-children': hasChildren,
    'nav-item--nested': level > 0
  }">
    <div class="nav-item-content" :style="indentStyle" @click="handleClick($event)">
      <i :class="getItemIcon(item)" class="nav-icon"></i>
      <span class="nav-label" v-if="!isMini">{{ displayName }}</span>

      <!-- Стрелка для элементов с детьми -->
      <i v-if="hasChildren && !isMini" class="pi pi-chevron-down nav-arrow"
        :class="{ 'nav-arrow--open': isOpen }"></i>
        <span v-if="!hasChildren && counter !== undefined" class="nav-counter">
          {{ counter }}
        </span>
    </div>

    <!-- Рекурсивное отображение детей -->
    <div v-if="hasChildren && !isMini" v-show="isOpen" class="nav-children">
      <NavMenuItem v-for="child in item.children" :key="child.branch_id" :item="child" :level="level + 1"
        :is-mini="isMini" @item-click="$emit('item-click', $event)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { MenuTreeBulkKey, type MenuTreeBulkContext } from '@/components/app/menuTreeBulk'
import { registryFormIdFromSlotId } from '@/common/utils/registryFormIdFromSlotId'
import { useMenu } from '@/common/composables/useMenu'
import { useMenuFocusStore } from '@/stores/menuFocus'
import { storeToRefs } from 'pinia'
import { computed, inject, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

interface MenuItem {
  branch_id: number
  name: string
  /** false = collapsed until user/route opens (server `<open>0</open>`); omitted = expanded */
  open?: boolean
  children?: MenuItem[]
}

interface Props {
  item: MenuItem
  level?: number
  isMini?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  level: 0,
  isMini: false
})

const emit = defineEmits<{
  'item-click': [payload: { item: MenuItem; event: MouseEvent }]
}>()

const route = useRoute()
const bulk = inject(MenuTreeBulkKey, null) as MenuTreeBulkContext | null
const menuFocusStore = useMenuFocusStore()
const { focusedMenuId, focusEpoch } = storeToRefs(menuFocusStore)
const rootRef = ref<HTMLElement | null>(null)

function branchInitiallyExpanded(menuItem: MenuItem): boolean {
  if (!menuItem.children?.length) return false
  return menuItem.open !== false
}

const isOpen = ref(branchInitiallyExpanded(props.item))
const { getItemIcon, getItemCounter, formatItemName } = useMenu()

const hasChildren = computed(() => {
  return props.item.children && props.item.children.length > 0
})
const displayName = computed(() => formatItemName(props.item || ''))
const counter = computed(() => getItemCounter(props.item))

const hasActiveChildren = computed(() => {
  const activeMenuId = getActiveMenuRouteId()
  if (activeMenuId && hasActiveNode(props.item, activeMenuId)) return true
  if (focusedMenuId.value && hasActiveNode(props.item, focusedMenuId.value)) return true
  return false
})

watch(hasActiveChildren, (newVal) => {
  if (bulk?.suppressRouteExpansion.value) return
  if (newVal) {
    isOpen.value = newVal
  }
}, {immediate: true});

watch(
  () => bulk?.collapseSignal.value,
  () => {
    if (!bulk || !hasChildren.value) return
    isOpen.value = false
  },
)

watch(
  () => bulk?.expandSignal.value,
  () => {
    if (!bulk || !hasChildren.value) return
    isOpen.value = true
  },
)

watch(
  [focusedMenuId, focusEpoch],
  async ([id]) => {
    if (!id || String(props.item.branch_id) !== id) return
    if (hasChildren.value) isOpen.value = true
    await nextTick()
    rootRef.value?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  },
  { immediate: true },
)


function hasActiveNode(node: MenuItem, currentId: string): boolean {
  let res = false;

  if (node.branch_id.toString() === currentId) {
    res = true;
  }

  if (node.children && node.children.length > 0) {
    node.children.forEach(node2 => {
      if (hasActiveNode(node2, currentId))
        res = true;
    });
  }
  return res;
}

const isFocusTarget = computed(() => {
  return Boolean(props.item.branch_id && focusedMenuId.value === props.item.branch_id.toString())
})

const isActive = computed(() => {
  const activeMenuId = getActiveMenuRouteId()
  if (props.item.branch_id && activeMenuId === props.item.branch_id.toString()) return true
  return isFocusTarget.value
})

const indentStyle = computed(() => {
  return {
    paddingLeft: `${props.level * 1.5 + 1.5}rem`
  }
})

function getActiveMenuRouteId(): string {
  if (route.name !== 'platform' && route.name !== 'document') return ''
  const routeId = route.params.id != null ? String(route.params.id) : ''
  if (!routeId) return ''
  return registryFormIdFromSlotId(routeId)
}

const handleClick = (event: MouseEvent) => {
  if (hasChildren.value) {
    if (bulk) {
      bulk.suppressRouteExpansion.value = false
    }
    isOpen.value = !isOpen.value
  }
  emit('item-click', { item: props.item, event })
}

</script>

<style scoped lang="scss">
.nav-item {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.2s ease;

  &-content {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }

  &--active {
    .nav-item-content {
      background: rgba(255, 255, 255, 0.08);
      border-right: none;
      border-left: 3px solid var(--primary-color, #ffffff);
    }
  }

  &--focus-target {
    .nav-item-content {
      outline: 1px solid rgba(255, 255, 255, 0.55);
      outline-offset: -1px;
    }
  }

  &--nested {
    .nav-item-content {
      font-size: 0.9rem;
      border-left: 2px solid rgba(255, 255, 255, 0.2);

      .nav-icon {
        font-size: 1rem;
      }
    }
  }

  &--has-children {
    .nav-item-content {
      font-weight: 600;
    }
  }
}

.nav-children {
  background: rgba(0, 0, 0, 0.1);
  margin-left: 0rem;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-icon {
  font-size: 1.25rem;
  margin-right: 0.5rem;
  transition: margin-right 0.3s ease;
  flex-shrink: 0;
}

.nav-label {
  font-weight: 500;
  font-size: 94%;
  transition: all 0.3s ease;
  flex: 1;
}

.nav-arrow {
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  margin-left: 0.1rem;

  &--open {
    transform: rotate(180deg);
  }
}

.nav-counter {
  margin-left: auto;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
}
</style>
